import * as Yup from "yup"

import type { AppLocalization } from "../../../../../modules/localization/appLocalization"
import { attendeeInitValues } from "../../../dialogs/createUpdateBookingDialog/attendees/attendeesForm/CreateUpdateAttendeesForm"
import { CreateUpdateBookingResourceTableSetups } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { DynamicConfigKey, BookingConfig, BookingCustomFieldsGroup, ExtendedDynamicConfigKey } from "./formTypes"
import { SelectedMainUserState } from "../../createOrderTypes"
import { buildFieldSchema } from "./helpers"
import { applyFieldFallback } from "./applyFieldFallback"
import { getAllConfigFields } from "./formLib"
import { InitialValuesState } from "../resource/ResourceBookingDraftContext"

const initialValuesMapping: Partial<Record<ExtendedDynamicConfigKey, (init: InitialValuesState) => any>> = {
    [DynamicConfigKey.MEETING_NAME]: (init) => init.meetingName,
    [DynamicConfigKey.PRIVATE_MEETING]: (init) => init.isPrivate || true,
    [DynamicConfigKey.EXTERNAL_MEETING]: (init) => init.isExternal || false,
    [DynamicConfigKey.MEETING_RESPONSIBLE]: ({ organizers }) => organizers ?? undefined,
    [DynamicConfigKey.ADD_PARTICIPANTS_FORM]: ({ attendees }) => (attendees?.length ? attendees : [attendeeInitValues]),
    [DynamicConfigKey.SERVICE_CENTER_MESSAGE]: (init) => init?.messageToServiceCenter || "",
}

export function generateBookingInitialValues(
    config: BookingConfig,
    customFields: BookingCustomFieldsGroup,
    initialValues: InitialValuesState | null,
    user: SelectedMainUserState,
): Record<ExtendedDynamicConfigKey | string, any> {
    const allFields = getAllConfigFields(config).filter(({ active }) => active)

    const initConfigValues = allFields.reduce(
        (acc, { key, inputField, selectValues }) => {
            if (key === DynamicConfigKey.MEETING_RESPONSIBLE) {
                acc[key] = {
                    name: user.displayName,
                    id: user.userId,
                }
                return acc
            }

            if (inputField) {
                applyFieldFallback(acc, inputField, key, selectValues)
            }
            return acc
        },
        {} as Record<ExtendedDynamicConfigKey, any>,
    )

    const mergedValues = { ...initConfigValues }

    if (allFields.some((f) => f.key === DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE)) {
        mergedValues[DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE] ??= false
    }

    if (initialValues) {
        ;(Object.keys(mergedValues) as DynamicConfigKey[]).forEach((key) => {
            const getter = initialValuesMapping[key]
            if (!getter) return

            const value = getter(initialValues)

            // Skip updating mergedValues if:
            // 1. The value is undefined (no update necessary)
            // 2. The current merged value and the new value have the same id
            //    (to prevent unnecessary overwriting of equivalent objects and
            //    to avoid causing extra re-renders or infinite loops in Formik)
            // This is important because Formik tracks object references strictly,
            // and even minor unnecessary changes can trigger full form resets or reinitializations.

            if (value === undefined || (value?.id && mergedValues[key]?.id === value?.id)) {
                return
            }

            mergedValues[key] = value
        })
    }

    const hasTableArrangements = allFields.some(({ key }) => key === DynamicConfigKey.TABLE_ARANGEMENTS)

    const processedResources: CreateUpdateBookingResourceTableSetups[] = initialValues?.resources
        ? initialValues.resources.map(({ id, tableSetups = [] }) => ({
              id,
              tableSetups: hasTableArrangements ? [] : tableSetups,
          }))
        : []

    return { ...mergedValues, ...customFields, resources: processedResources }
}

export function generateBookingValidationSchema(
    config: BookingConfig,
    l10n: AppLocalization,
): Yup.ObjectSchema<Record<ExtendedDynamicConfigKey, any>> {
    const shape: Partial<Record<ExtendedDynamicConfigKey, Yup.Schema<any>>> = {}

    const allFields = getAllConfigFields(config)

    const activeFieldKeys = new Set(allFields.filter(({ active }) => active).map(({ key }) => key))

    allFields.forEach((config) => {
        const field = config.inputField
        const key = config.key
        const label = config.label || config.key

        if (!activeFieldKeys.has(key)) return

        shape[key] = buildFieldSchema(field, label, l10n)
    })

    return Yup.object().shape(shape as Record<DynamicConfigKey, Yup.Schema<any>>)
}
